import { useLiveData } from '@/hooks/useLiveData';
import { getReferenceString } from '@medplum/core';
import { Communication, DocumentReference, OperationOutcome, Organization } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react';

export interface UseDocumentsResult {
  documents: DocumentReference[];
  documentsLoading: boolean;
  documentsError: OperationOutcome | Error | undefined;
  refreshDocuments: () => void;
  pauseLiveUpdates: () => void;
}

export function useDocuments(organization: Organization | undefined): UseDocumentsResult {
  const medplum = useMedplum();
  const [areLiveUpdatesPaused, setAreLiveUpdatesPaused] = useState(false);
  const [documents, setDocuments] = useState<DocumentReference[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<OperationOutcome | Error | undefined>(undefined);

  const queryParams = useMemo(() => {
    if (!organization) return undefined;
    return {
      recipient: getReferenceString(organization),
      _sort: '-sent',
      _count: 1000,
    };
  }, [organization]);

  const {
    data: communications,
    loading: commLoading,
    error: commError,
    refresh: refreshComm,
  } = useLiveData('Communication', queryParams, {
    pauseLiveUpdates: areLiveUpdatesPaused,
  });

  // Extract DocumentReferences from Communications with attachments
  useEffect(() => {
    if (!communications || communications.length === 0) {
      setDocuments([]);
      setDocumentsLoading(commLoading);
      setDocumentsError(commError);
      return;
    }

    const extractDocuments = async () => {
      setDocumentsLoading(true);
      try {
        const docRefs: DocumentReference[] = [];

        for (const comm of communications as Communication[]) {
          if (comm.payload) {
            for (const payload of comm.payload) {
              if (payload.contentReference?.reference) {
                try {
                  const docRef = await medplum.readReference<DocumentReference>(
                    payload.contentReference as any as { reference: string }
                  );
                  if (!docRefs.find((d) => d.id === docRef.id)) {
                    docRefs.push(docRef);
                  }
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error('Error reading document reference:', error);
                }
              }
            }
          }
        }

        // Sort by date (newest first)
        docRefs.sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });

        setDocuments(docRefs);
        setDocumentsError(undefined);
      } catch (error) {
        setDocumentsError(error as Error);
        setDocuments([]);
      } finally {
        setDocumentsLoading(false);
      }
    };

    extractDocuments();
  }, [communications, commLoading, commError, medplum]);

  const pauseLiveUpdates = useCallback(() => {
    setAreLiveUpdatesPaused(true);
  }, []);

  const refreshDocuments = useCallback(() => {
    setAreLiveUpdatesPaused(false);
    refreshComm();
  }, [refreshComm]);

  return {
    documents,
    documentsLoading: documentsLoading || commLoading,
    documentsError: documentsError || commError,
    refreshDocuments,
    pauseLiveUpdates,
  };
}
